/// <amd-module name="Wit/WorkItems/Utilities/WorkItemRelationUtilities" />
import { equals } from "VSS/Core/Util/String";
import { toLookup } from "VSS/Platform/Util/Array";
import { ResourceLinkType, WorkItemArtifactLink, WorkItemAttachment } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { WorkItemRelation } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { LinkTypeConstants } from "../LinkConstants";
import { IWorkItemRelations, PartialWorkItemLinkedItem } from "../WorkItemContracts";

/*
 * Return all items in A that are not present in B
 * @param a map of type { [key: string]: T }
 * @param b Array of type { [key: string]: T } which will be modified with contents from a
 * @param compare Compare method to check equality between two objects of type T
 */
export function diffMaps<T>(a: { [key: string]: T }, b: { [key: string]: T }, compare: { (a: T, b: T): boolean }): { [key: string]: T } {
    if (!b || Object.keys(b).length === 0) {
        return a || {};
    }
    if (!a || Object.keys(a).length === 0) {
        return {};
    }
    const diffMap: { [key: string]: T } = {};
    for (const key of Object.keys(a)) {
        if (!b[key] || !compare(b[key], a[key])) {
            diffMap[key] = a[key];
        }
    }
    return diffMap;
}

/*
 * Merge all elements in A into B
 * @param a Array of type T
 * @param b Array of type T which will eb modified with contents from a
 * @param keySelector KeySelector of type T pased in from caller
 * @param compare Compare method to check equality between two objects of type T
 */
export function mergeArrays<T>(a: T[], b: T[], keySelector: (item: T) => string, compare: { (a: T, b: T): boolean }) {
    const itemMapA = toLookup(a, keySelector);
    const itemMapB = toLookup(b, keySelector);
    const itemsDiffMap: { [key: string]: T } = diffMaps(itemMapB, itemMapA, compare);
    const itemsToAdd: { [key: string]: T } = diffMaps(itemMapA, itemMapB, compare);
    const itemsToDelete: { [key: string]: T } = {};
    for (const key of Object.keys(itemsDiffMap)) {
        if (!itemsToAdd[key]) {
            itemsToDelete[key] = itemsDiffMap[key];
        }
    }
    // Find items which need to be deleted and delete them from the map.
    for (const key of Object.keys(itemsToDelete)) {
        if (key) {
            if (itemMapB[key]) {
                delete itemMapB[key];
            }
        }
    }
    // Find items which need to be added
    for (const key of Object.keys(itemsToAdd)) {
        if (key) {
            if (!itemMapB[key]) {
                itemMapB[key] = itemsToAdd[key];
                // Add to B array as well
                b.push(itemsToAdd[key]);
            } else {
                itemMapB[key] = itemsToAdd[key];
            }
        }
    }
    // Update B array
    for (let index = b.length - 1; index >= 0; index--) {
        if (b[index]) {
            const key = keySelector(b[index]);
            // Delete item from B array
            if (!itemMapB[key]) {
                b.splice(index, 1);
            }
            // Update items
            if (itemMapB[key] && !compare(b[index], itemMapB[key])) {
                b[index] = itemMapB[key];
            }
        }
    }
}

export function areLinksEquivalent(a: PartialWorkItemLinkedItem, b: PartialWorkItemLinkedItem): boolean {
    return (
        a.targetWorkItemId === b.targetWorkItemId &&
        equals(a.referenceName, b.referenceName, true) &&
        a.comment === b.comment &&
        a.isLocked === b.isLocked &&
        a.name === b.name &&
        a.targetProjectId === b.targetProjectId
    );
}

export function areAttachmentsEquivalent(a: WorkItemAttachment, b: WorkItemAttachment): boolean {
    return a.comment === b.comment && equals(a.id, b.id, true);
}

export function areArtifactsEquivalent(a: WorkItemArtifactLink, b: WorkItemArtifactLink): boolean {
    return a.comment === b.comment && a.location === b.location && (a.name, b.name, true);
}

export function splitWorkItemRelations(relations: WorkItemRelation[] | undefined): IWorkItemRelations {
    const attachments: WorkItemAttachment[] = [];
    const links: PartialWorkItemLinkedItem[] = [];
    const artifactLinks: WorkItemArtifactLink[] = [];

    if (relations) {
        relations.forEach(relation => {
            if (isAttachment(relation)) {
                attachments.push({
                    url: relation.url,
                    id: convertAttachmentUrlToId(relation.url),
                    fileName: relation.attributes && relation.attributes.name,
                    comment: relation.attributes && relation.attributes.comment,
                    size: relation.attributes && relation.attributes.resourceSize
                });
            } else if (isWorkItemArtifactLink(relation)) {
                artifactLinks.push({
                    location: relation.url,
                    name: relation.attributes && relation.attributes.name,
                    comment: relation.attributes && relation.attributes.comment,
                    resourceLinkType: isHyperlink(relation) ? ResourceLinkType.Hyperlink : ResourceLinkType.ArtifactLink
                });
            } else if (isWorkItemLink(relation)) {
                links.push({
                    targetWorkItemId: convertTargetUrlToWorkItemId(relation.url),
                    referenceName: relation.rel,
                    comment: relation.attributes && relation.attributes.comment,
                    name: relation.attributes && relation.attributes.name,
                    isLocked: relation.attributes && relation.attributes.isLocked,
                    targetProjectId: relation.attributes && relation.attributes.targetProjectId
                });
            }
        });
    }

    return { attachments, links, artifactLinks };
}

export function isAttachment(relation: WorkItemRelation): boolean {
    return relation.rel === LinkTypeConstants.AttachmentReferenceName;
}

export function isWorkItemArtifactLink(relation: WorkItemRelation): boolean {
    return relation.rel === LinkTypeConstants.ArtifactLinkReferenceName;
}

export function isHyperlink(relation: WorkItemRelation): boolean {
    return relation.rel === LinkTypeConstants.HyperlinkReferenceName;
}

export function isWorkItemLink(relation: WorkItemRelation): boolean {
    return !isAttachment(relation) && !isWorkItemArtifactLink(relation) && !isHyperlink(relation);
}

function convertTargetUrlToWorkItemId(url: string): number {
    const lastIndexOfSlash = url.lastIndexOf("/") + 1;
    if (lastIndexOfSlash < url.length) {
        const workItemIdString = url.slice(url.lastIndexOf("/") + 1);
        return Number(workItemIdString);
    } else {
        // Else throw. This should not happen in production, but should help us catch early bugs
        throw new Error("url does not contain work item id.");
    }
}

function convertAttachmentUrlToId(url: string): string {
    const lastIndexOfSlash = url.lastIndexOf("/") + 1;
    if (lastIndexOfSlash < url.length) {
        const attachmentId = url.slice(url.lastIndexOf("/") + 1);
        return attachmentId;
    } else {
        // Else throw. This should not happen in production, but should help us catch early bugs
        throw new Error("url does not contain attachment id.");
    }
}
